var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { getDiscoverSubjects, WhatsNewProvider } from "@piiqtechnologies/ui-lib";
import { AnalyticsArea } from "@analytics-setup/events";
import { useTrackEvent } from "@hooks/useTrackEvent";
import { useWhatsNewEnabled } from "@hooks/useWhatsNewEnabled";
import { useOptionalActiveOrganization, useOrgPath } from "@stores/ActiveOrganizationContext";
import { useSidebarNavContext } from "@stores/SidebarNavContext";
import { PATHS } from "@constants/Paths";
import { getWhatsNewPlacement } from "@utils/whatsNewPlacement";
/**
 * Reads a link from the API, which sends full URLs; an older path-only one ("/community/c1") is still
 * read as Discover's. A Discover link copied from the address bar names the admin's own organization
 * (/o/:orgId/...): that segment is dropped so the customer's is used. Anything but http(s) is ignored.
 */
const readLink = (href) => {
    let url;
    try {
        url = new URL(href, window.location.origin);
    }
    catch (_a) {
        return null;
    }
    if (url.protocol !== "https:" && url.protocol !== "http:")
        return null;
    if (url.origin !== window.location.origin)
        return { site: url.href };
    const path = url.pathname.replace(/^\/o\/[^/]+(?=\/|$)/, "") || "/";
    return { path: `${path}${url.search}${url.hash}` };
};
const COMMUNITY_PAGE = /^(?:\/o\/[^/]+)?\/community\/([0-9a-fA-F]{24})/;
const GENERIC_COMMUNITY = /^\/community(?=[?#]|$)/;
/**
 * Each What's New event as Discover tracks it, `Discover | What's New | <Action>` (see
 * docs/analytics-tracking-plan.md). Its properties ride along: the page (`placement`) and, for one
 * announcement, `announcementId`.
 */
const WHATS_NEW_ACTIONS = {
    drawer_opened: "Drawer Opened",
    drawer_entry_clicked: "Drawer Entry Clicked",
    drawer_load_more: "Drawer Load More",
    demo_started: "Demo Started",
    demo_completed: "Demo Completed",
};
// The pop-up or drawer failed to render and closed; the page carries on, so report it.
const reportWhatsNewError = (error) => Sentry.captureException(error, { tags: { feature: "whats-new" } });
/**
 * Discover's side of What's New: the placement comes from the route, links go through the router
 * inside the active organization, what the customer does is tracked, and nothing mounts while the
 * feature flag is off — which is also what hides the sidebar row.
 */
export const DiscoverWhatsNew = ({ children }) => {
    var _a;
    const enabled = useWhatsNewEnabled();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const orgPath = useOrgPath();
    const activeOrgId = (_a = useOptionalActiveOrganization()) === null || _a === void 0 ? void 0 : _a.activeOrgId;
    const { activeCommunityId } = useSidebarNavContext();
    const track = useTrackEvent();
    const handleEvent = useCallback((_a) => {
        var { type } = _a, properties = __rest(_a, ["type"]);
        return track(AnalyticsArea.WhatsNew, WHATS_NEW_ACTIONS[type], properties);
    }, [track]);
    const resolveCommunityPath = useCallback((path) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        const current = activeCommunityId !== null && activeCommunityId !== void 0 ? activeCommunityId : (_a = pathname.match(COMMUNITY_PAGE)) === null || _a === void 0 ? void 0 : _a[1];
        if (current)
            return path.replace(GENERIC_COMMUNITY, `/community/${current}`);
        try {
            const response = yield getDiscoverSubjects(Object.assign({ limit: 1, sort: "createdAt" }, (activeOrgId && { organizationId: activeOrgId })));
            const latest = (_c = (_b = response.data) === null || _b === void 0 ? void 0 : _b.subjects) === null || _c === void 0 ? void 0 : _c[0];
            if (latest)
                return path.replace(GENERIC_COMMUNITY, `/community/${latest._id}`);
        }
        catch (_d) {
            // No list to pick from: home is the next best page.
        }
        return PATHS.HOME;
    }), [activeCommunityId, activeOrgId, pathname]);
    // Links into Discover go through the router, inside the active organization (marketing writes them
    // without the /o/:orgId segment); learn-more links and demos are other sites, in a new tab.
    const handleNavigate = useCallback((href) => {
        const link = href ? readLink(href) : null;
        if (!link)
            return;
        if ("site" in link) {
            window.open(link.site, "_blank", "noopener,noreferrer");
            return;
        }
        if (GENERIC_COMMUNITY.test(link.path)) {
            void resolveCommunityPath(link.path).then((path) => navigate(orgPath(path)));
            return;
        }
        navigate(orgPath(link.path));
    }, [navigate, orgPath, resolveCommunityPath]);
    if (!enabled)
        return React.createElement(React.Fragment, null, children);
    return (React.createElement(WhatsNewProvider, { product: "discover", placement: getWhatsNewPlacement(pathname), onNavigate: handleNavigate, sessionScope: activeOrgId, onError: reportWhatsNewError, onEvent: handleEvent }, children));
};
export default DiscoverWhatsNew;
